import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Users,
  Calendar,
  Briefcase,
  Target,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const CompanyPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <Building2 className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-2xl font-bold text-gray-900">Reko HR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/#features" className="text-gray-700 hover:text-primary-600">
                {t('nav.features')}
              </Link>
              <Link to="/#platforms" className="text-gray-700 hover:text-primary-600">
                {t('nav.platforms')}
              </Link>
              <Link to="/#pricing" className="text-gray-700 hover:text-primary-600">
                {t('nav.pricing')}
              </Link>
              <Link to="/about" className="text-primary-600 font-semibold">
                {t('nav.about')}
              </Link>
              <LanguageSwitcher />
            </nav>
            <div className="flex items-center space-x-4">
              <div className="md:hidden">
                <LanguageSwitcher />
              </div>
              <Link
                to="/company-request"
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
              >
                {t('nav.enterpriseInquiry')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-blue-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl font-bold mb-4">{t('company.parent.title')}</h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            {t('company.parent.subtitle')}
          </p>
        </div>
      </section>

      {/* Company Description */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <p className="text-lg text-gray-700 leading-relaxed text-center max-w-4xl mx-auto">
              {t('company.parent.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            {t('company.overview.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-sm text-gray-500 mb-1">{t('company.overview.industry')}</h3>
              <p className="text-lg font-semibold text-gray-900">{t('company.overview.industryValue')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-sm text-gray-500 mb-1">{t('company.overview.size')}</h3>
              <p className="text-lg font-semibold text-gray-900">{t('company.overview.sizeValue')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-sm text-gray-500 mb-1">{t('company.overview.founded')}</h3>
              <p className="text-lg font-semibold text-gray-900">{t('company.overview.foundedValue')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-sm text-gray-500 mb-1">{t('company.overview.headquarters')}</h3>
              <p className="text-lg font-semibold text-gray-900">{t('company.overview.headquartersValue')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Office Location */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <MapPin className="h-6 w-6 text-primary-600 mr-2" />
                {t('company.location.title')}
              </h2>
              <div className="space-y-2 text-gray-700">
                <p className="font-semibold text-lg">{t('company.location.address')}</p>
                <p>{t('company.location.area')}</p>
                <p>{t('company.location.city')}</p>
                <p className="text-primary-600 font-medium">{t('company.location.country')}</p>
              </div>
              {/* Google Maps Embed */}
              <div className="mt-6 rounded-lg overflow-hidden h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3966.2665632969196!2d106.81093031476891!3d-6.227067295492894!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e69f3fdf7b47eb3%3A0x5a8b9f3f3f3f3f3f!2sEquity%20Tower!5e0!3m2!1sen!2sid!4v1234567890123"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Phone className="h-6 w-6 text-primary-600 mr-2" />
                {t('company.contact.title')}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm text-gray-500">{t('company.contact.phone')}</h3>
                    <a href="tel:+62215155141" className="text-lg font-semibold text-gray-900 hover:text-primary-600">
                      +62 21 5155141
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <Mail className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm text-gray-500">{t('company.contact.email')}</h3>
                    <a href="mailto:contact@reko-hr.com" className="text-lg font-semibold text-gray-900 hover:text-primary-600">
                      contact@reko-hr.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <Globe className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm text-gray-500">{t('company.contact.website')}</h3>
                    <a
                      href="https://www.reko-hr.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-semibold text-gray-900 hover:text-primary-600 flex items-center"
                    >
                      www.reko-hr.com
                      <ExternalLink className="h-4 w-4 ml-1" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Parent Company */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm text-gray-500 mb-2">Parent Company</h3>
                <a
                  href="https://www.bccard.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-semibold text-primary-600 hover:text-primary-700 flex items-center"
                >
                  BC Card Co., Ltd.
                  <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Product Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('company.product.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('company.product.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-gradient-to-r from-primary-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Target className="h-10 w-10 text-primary-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('company.mission.title')}</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {t('company.mission.description')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">{t('company.cta.title')}</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            {t('company.cta.description')}
          </p>
          <Link
            to="/company-request"
            className="bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center text-lg font-semibold"
          >
            {t('company.cta.button')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Building2 className="h-8 w-8 text-primary-400" />
                <span className="ml-2 text-2xl font-bold">Reko HR</span>
              </div>
              <p className="text-gray-400">{t('footer.tagline')}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.product')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/#features" className="hover:text-white">{t('nav.features')}</Link></li>
                <li><Link to="/#platforms" className="hover:text-white">{t('nav.platforms')}</Link></li>
                <li><Link to="/#pricing" className="hover:text-white">{t('nav.pricing')}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.support')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">{t('footer.helpCenter')}</a></li>
                <li><a href="#" className="hover:text-white">{t('footer.apiDocs')}</a></li>
                <li><Link to="/#contact" className="hover:text-white">{t('footer.contactUs')}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.company')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white">{t('footer.aboutUs')}</Link></li>
                <li><a href="#" className="hover:text-white">{t('footer.privacyPolicy')}</a></li>
                <li><a href="#" className="hover:text-white">{t('footer.termsOfService')}</a></li>
              </ul>
              <div className="mt-4">
                <LanguageSwitcher variant="footer" />
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CompanyPage;
